import { Button, Modal } from '@orobix/orobix-fe-components'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import { post } from '../../utils/api'
import { handleApiErrors } from '../../utils/apiErrors'
import { getQueryString } from '../../utils/queryString'
import { useStyles } from './BulkActionByFilterButton.styles'
import { toApiFiltering } from './DrugRegistryList/useDrugRegistry'

type BulkActionByFilterButtonProps = {
  filtering: Record<string, string | undefined>
  resultCount: number
  // `useDrugRegistry` keeps the previous page of data (and therefore the
  // previous `count`) while a new `filtering` value is still being fetched.
  // Since the table's text filters commit on blur, a user can type a filter
  // and click straight through to this button before the new count has
  // landed, which would show — and confirm — a count belonging to the
  // *previous* filter.
  isFetching: boolean
}

export const BulkActionByFilterButton = ({
  filtering,
  resultCount,
  isFetching,
}: BulkActionByFilterButtonProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const [modal, setModal] = useState(false)
  const [machinable, setMachinable] = useState(true)
  const queryClient = useQueryClient()

  const hasActiveFilter = Object.values(filtering).some(
    (value) => value !== undefined && value !== ''
  )

  // The radio selection is per-attempt state: reopening the modal after an
  // abandoned attempt must start from the default again, not silently
  // inherit whatever the previous, dismissed attempt had selected.
  const closeModal = () => {
    setModal(false)
    setMachinable(true)
  }

  const { mutate, isLoading } = useMutation(
    ['drug-registry', 'bulk-action-by-filter'],
    () =>
      // `filtering`'s range/lookup keys (e.g. `heightGte`, `minsanIcontains`)
      // are camelCase, but the backend's django-filter param names use
      // double-underscore lookups (e.g. `height__gte`, `minsan__icontains`).
      // `getQueryString` only preserves a key unchanged when it already
      // contains an underscore, so passing `filtering` to it directly would
      // silently turn `heightGte` into `height_gte` (single underscore) — a
      // param the backend does not recognise — instead of the real
      // `height__gte`. `toApiFiltering` (already used by the visible list's
      // own query in `useDrugRegistry.ts`) applies the same rewrite here, so
      // the bulk action matches exactly the same rows the confirmation
      // count and the visible table are both based on.
      post<{ updatedCount: number }>(
        `/drug-registry/bulk-action-by-filter/?${getQueryString(
          toApiFiltering(filtering)
        )}`,
        { machinable }
      ),
    {
      retry: false,
      onError: handleApiErrors,
      onSuccess: (result) => {
        queryClient.invalidateQueries(['drug-registry'])
        toast.success(
          t('{{ count }} drugs updated', { count: result.updatedCount })
        )
        closeModal()
      },
    }
  )

  return (
    <>
      <Button
        label={t('Apply to current filter')}
        // A filter matching nothing makes the whole action a no-op write, and
        // an in-flight refetch means `resultCount` still describes the
        // previous filter — neither is a state the modal should open in.
        disabled={!hasActiveFilter || resultCount === 0 || isFetching}
        onClick={() => setModal(true)}
      />
      {modal && (
        <Modal
          title={t('Apply to current filter')}
          onCloseRequest={closeModal}
          footer={
            <div className={classes.footerButtonWrapper}>
              <Button label={t('Cancel')} onClick={closeModal} />
              <Button
                label={t('Confirm')}
                type="action"
                loading={isLoading}
                // The orobix `Button`'s `loading` prop only swaps the icon; it
                // does not set the DOM `disabled` attribute. Without this the
                // button stays clickable while the request is in flight, and a
                // double click fires two full-filtered-set bulk writes (and two
                // success toasts).
                disabled={isLoading}
                onClick={() => mutate()}
              />
            </div>
          }
        >
          <p>
            {t(
              'This will update {{ count }} drugs matching the current filter',
              { count: resultCount }
            )}
          </p>
          <label htmlFor="bulk-action-machinable-true">
            <input
              id="bulk-action-machinable-true"
              type="radio"
              // Both inputs share one `name` so they form a single radio
              // group: without it each is its own one-option group, which
              // breaks arrow-key navigation and assistive-tech announcement
              // of the "1 of 2" position.
              name="bulkActionMachinable"
              checked={machinable}
              onChange={() => setMachinable(true)}
            />
            {t('Machinable')}
          </label>
          <label htmlFor="bulk-action-machinable-false">
            <input
              id="bulk-action-machinable-false"
              type="radio"
              name="bulkActionMachinable"
              checked={!machinable}
              onChange={() => setMachinable(false)}
            />
            {t('Not machinable')}
          </label>
        </Modal>
      )}
    </>
  )
}
